/**
 * Cached Community host context.
 *
 * The backend's /community/context tells COS whether the Community module
 * is enabled for this deployment, and (when enabled) the websocket + media
 * hosts the embedded SPA needs. The shell (for the nav rows) and
 * cos-community-page (for the mount) share ONE fetch per session via this
 * module-level cache — the same singleton pattern as services/
 * circulate-products.ts. Failures are never cached, so a transient error
 * doesn't permanently hide the module; the next caller retries.
 */
import { api } from "./api.js";

export interface CommunityContext {
	enabled: boolean;
	ws_url: string | null;
	media_base: string | null;
}

const DISABLED: CommunityContext = { enabled: false, ws_url: null, media_base: null };

let cached: Promise<CommunityContext> | null = null;

/** The raw context promise. Throws on error (and clears the cache). */
export function loadCommunityContext(force = false): Promise<CommunityContext> {
	if (!cached || force) {
		cached = (api.getCommunityContext() as Promise<CommunityContext>).catch((e) => {
			cached = null; // don't cache failures — the next caller retries
			throw e;
		});
	}
	return cached;
}

/**
 * The context, degrading to disabled on any error — for callers (the nav)
 * that want a boolean, not an exception.
 */
export async function communityContext(): Promise<CommunityContext> {
	try {
		return await loadCommunityContext();
	} catch {
		return DISABLED;
	}
}

/** Convenience: is the Community module enabled for this deployment? */
export async function communityEnabled(): Promise<boolean> {
	return (await communityContext()).enabled;
}
